import React from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { FormField } from '../forms/FormField'
import { SelectField } from '../forms/SelectField'
import type { FnaFormValues } from '../../contracts/fnaFormSchema'
import { YES_NO_OPTIONS } from './profileStepOptions'

/**
 * Step 7: Debts with dynamic add/remove rows via `useFieldArray`.
 *
 * @returns JSX.Element The debts step
 */
export function DebtsStep(): JSX.Element {
  const { control } = useFormContext<FnaFormValues>()
  const hasDebts = useWatch<FnaFormValues, 'hasDebts'>({ name: 'hasDebts' })
  const { fields, append, remove } = useFieldArray<FnaFormValues, 'debts'>({
    control,
    name: 'debts',
  })

  return (
    <>
      <h2>Debts</h2>
      <SelectField<FnaFormValues>
        name="hasDebts"
        label="Do you have any debts?"
        options={YES_NO_OPTIONS}
      />
      {hasDebts === 'yes' && (
        <div className="debts-list">
          {fields.map((field, idx) => (
            <div
              key={field.id}
              style={{
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <FormField<FnaFormValues>
                name={`debts.${idx}.debtName` as Path<FnaFormValues>}
                label={`Debt #${idx + 1} name`}
              />
              <FormField<FnaFormValues>
                name={`debts.${idx}.currentBalance` as Path<FnaFormValues>}
                label="Current balance"
                type="number"
                inputMode="decimal"
              />
              <FormField<FnaFormValues>
                name={`debts.${idx}.interestRate` as Path<FnaFormValues>}
                label="Interest rate (%)"
                type="number"
                inputMode="decimal"
                step="0.01"
              />
              <FormField<FnaFormValues>
                name={`debts.${idx}.minimumPayment` as Path<FnaFormValues>}
                label="Minimum monthly payment"
                type="number"
                inputMode="decimal"
              />
              <FormField<FnaFormValues>
                name={`debts.${idx}.debtType` as Path<FnaFormValues>}
                label="Type (e.g. credit card, student loan)"
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                className="nav-btn prev-btn"
                style={{ marginTop: '0.5rem' }}
              >
                Remove debt
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              append({
                debtName: '',
                currentBalance: 0,
                interestRate: undefined,
                minimumPayment: undefined,
                debtType: undefined,
              })
            }
            className="nav-btn next-btn"
          >
            + Add a debt
          </button>
        </div>
      )}
    </>
  )
}
